import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, SlidersHorizontal, Users, X, Crown, Share2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { matchesApi } from '../../api/matches'
import type { MatchResultDTO } from '../../types'
import ProfileCard from '../../components/ProfileCard'

type Tab = 'suggestions' | 'premium'

const tabs: { key: Tab; label: string }[] = [
  { key: 'suggestions', label: 'For You' },
  { key: 'premium',     label: 'Premium' },
]

interface Filters {
  minAge: string
  maxAge: string
  minHeightCm: string
  maxHeightCm: string
  educationLevel: string
  manglik: string
  familyType: string
  diet: string
  maritalStatus: string
  minIncomeLpa: string
}

const EMPTY_FILTERS: Filters = {
  minAge: '', maxAge: '', minHeightCm: '', maxHeightCm: '',
  educationLevel: '', manglik: '', familyType: '', diet: '', maritalStatus: '', minIncomeLpa: '',
}

function hasFilters(f: Filters) {
  return Object.values(f).some((v) => v !== '')
}

export default function MatchesPage() {
  const [tab, setTab] = useState<Tab>('suggestions')
  const [searchName, setSearchName] = useState('')
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS)
  const [activeFilters, setActiveFilters] = useState<Filters>(EMPTY_FILTERS)

  const setF = (k: keyof Filters, v: string) =>
    setPendingFilters((f) => ({ ...f, [k]: v }))

  const applyFilters = () => {
    // Members are 21+ — clamp both age bounds and reflect the clamped values in the inputs.
    const clampAge = (v: string, floor: number) => {
      const n = parseInt(v, 10)
      return Number.isNaN(n) ? v : String(Math.min(Math.max(n, floor), 80))
    }
    const minAge = clampAge(pendingFilters.minAge, 21)
    const maxAge = clampAge(pendingFilters.maxAge, Math.max(21, parseInt(minAge, 10) || 21))
    const clamped = { ...pendingFilters, minAge, maxAge }
    setPendingFilters(clamped)
    setActiveFilters(clamped)
    setShowFilterPanel(false)
  }

  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS)
    setActiveFilters(EMPTY_FILTERS)
  }

  const filtersActive = hasFilters(activeFilters)
  const qc = useQueryClient()

  const handleDismiss = (id: string) => {
    const key = ['matches', tab, activeFilters]
    // optimistic remove
    qc.setQueryData<MatchResultDTO[]>(key, (old) => old?.filter((m) => m.profile.id !== id))
    matchesApi.dismiss(id).catch(() => {
      toast.error('Could not remove — try again')
      qc.invalidateQueries({ queryKey: ['matches'] })
    })
    toast((t) => (
      <span className="flex items-center gap-3 text-sm">
        Removed from your feed
        <button
          className="font-semibold text-maroon-700"
          onClick={() => {
            toast.dismiss(t.id)
            matchesApi.undismiss(id).finally(() => qc.invalidateQueries({ queryKey: ['matches'] }))
          }}
        >
          Undo
        </button>
      </span>
    ))
  }

  const { data: matches, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['matches', tab, activeFilters],
    retry: (count, err) =>
      // Don't retry the premium gate — it's a 403 by design, not a transient failure
      (err as { response?: { status?: number } })?.response?.status === 403 ? false : count < 2,
    queryFn: () => {
      // If filters are active, always use the search endpoint
      if (filtersActive) {
        const params: Record<string, string | number> = {}
        if (activeFilters.minAge) params.minAge = Number(activeFilters.minAge)
        if (activeFilters.maxAge) params.maxAge = Number(activeFilters.maxAge)
        if (activeFilters.minHeightCm) params.minHeightCm = Number(activeFilters.minHeightCm)
        if (activeFilters.maxHeightCm) params.maxHeightCm = Number(activeFilters.maxHeightCm)
        if (activeFilters.educationLevel) params.educationLevel = activeFilters.educationLevel
        if (activeFilters.manglik) params.manglik = activeFilters.manglik
        if (activeFilters.familyType) params.familyType = activeFilters.familyType
        if (activeFilters.diet) params.diet = activeFilters.diet
        if (activeFilters.maritalStatus) params.maritalStatus = activeFilters.maritalStatus
        if (activeFilters.minIncomeLpa) params.minIncomeLpa = Number(activeFilters.minIncomeLpa)
        return matchesApi.search(params).then((r) => r.data.data)
      }
      if (tab === 'suggestions') return matchesApi.getSuggestions(0, 30).then((r) => r.data.data)
      return matchesApi.getPremiumPicks(0, 30).then((r) => r.data.data)
    },
  })

  const filtered = matches?.filter(
    (m) => !searchName || m.profile.fullName?.toLowerCase().includes(searchName.toLowerCase())
  )

  return (
    <div className="max-w-5xl mx-auto px-4 py-5">

      {/* Header */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="kicker">Curated for you</p>
          <h1 className="font-display font-semibold text-[30px] text-ink-900 mt-1">
            {tab === 'premium' && !filtersActive ? 'Premium Picks' : "Today's Matches"}
          </h1>
        </div>
        <div className="flex items-center gap-2 pb-1">
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-danger border border-danger/30 px-3 py-1.5 rounded-full"
            >
              <X size={11} /> Clear
            </button>
          )}
          <button
            onClick={() => { setPendingFilters({ ...activeFilters }); setShowFilterPanel(true) }}
            className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-3.5 py-2 rounded-full border transition-colors ${
              filtersActive
                ? 'text-maroon-700 border-gold-500/60 bg-ivory-100'
                : 'text-ink-400 border-ivory-300 hover:border-gold-500/60 hover:text-ink-600'
            }`}
          >
            <SlidersHorizontal size={13} strokeWidth={1.75} /> Filter
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={15} strokeWidth={1.75} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
        <input
          type="text"
          className="w-full bg-white rounded-full border border-ivory-300 pl-11 pr-4 py-3 text-sm font-medium text-ink-900 placeholder:text-ink-400/70 focus:outline-none focus:border-gold-500 focus:shadow-glow transition-all duration-200"
          placeholder="Search by name…"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
      </div>

      {/* Tab chips (hidden when filters active) */}
      {!filtersActive && (
        <div className="flex gap-2 overflow-x-auto pb-1 mb-5 -mx-4 px-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={tab === t.key ? 'chip-active' : 'chip-inactive'}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Result count */}
      {!isLoading && filtered != null && (
        <p className="text-[10px] font-semibold text-ink-400 uppercase tracking-[0.2em] mb-4">
          {filtered.length} profiles {filtersActive && '· filtered'}
        </p>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-ivory-100 rounded-2xl h-64 animate-pulse" />
          ))}
        </div>
      ) : isError && (error as { response?: { status?: number } })?.response?.status === 403 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-glow">
            <Crown size={26} strokeWidth={1.75} className="text-white" />
          </div>
          <div>
            <p className="font-display font-semibold text-xl text-ink-900">Premium Picks</p>
            <p className="text-sm text-ink-400 mt-1 max-w-xs">
              Hand-curated, high-compatibility matches — available with a Premium membership.
            </p>
          </div>
          <Link to="/subscription" className="btn-primary text-sm py-2.5 px-6">
            Upgrade to Premium
          </Link>
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <p className="font-display font-semibold text-lg text-ink-900">Something went wrong</p>
          <p className="text-sm text-ink-400">Could not load profiles. Check your connection.</p>
          <button onClick={() => refetch()} className="btn-primary text-sm py-2 px-5">Retry</button>
        </div>
      ) : filtered?.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((m) => (
            <ProfileCard
              key={m.profile.id}
              match={m}
              onDismiss={tab === 'suggestions' && !filtersActive ? handleDismiss : undefined}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-3 px-6">
          <div className="w-16 h-16 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center">
            <Users size={24} strokeWidth={1.5} className="text-gold-600/60" />
          </div>
          {filtersActive ? (
            <>
              <p className="font-display font-semibold text-lg text-ink-900">No profiles match your filters</p>
              <p className="text-sm text-ink-400 text-center">Try relaxing your filters to see more rishtey.</p>
            </>
          ) : (
            <>
              <p className="font-display font-semibold text-lg text-ink-900 text-center">You're one of our early members</p>
              <p className="text-sm text-ink-400 text-center max-w-xs">
                New rishtey join every day. Help our community grow — share Ahirwal Matrimony with relatives and families in your samaj looking for a match.
              </p>
              <button
                onClick={async () => {
                  const text = 'Ahirwal Matrimony — a trusted rishta platform just for our Ahirwal Yadav (Ahir) community. Verified profiles, gotra-aware matching, privacy-first.'
                  const url = 'https://ahirwalmatrimony.in'
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: 'Ahirwal Matrimony', text, url })
                    } else {
                      await navigator.clipboard.writeText(`${text} ${url}`)
                      toast.success('Invite link copied — share it with your community')
                    }
                  } catch { /* user cancelled share sheet — ignore */ }
                }}
                className="mt-1 inline-flex items-center gap-2 bg-maroon-700 text-ivory-25 font-semibold text-sm px-5 py-2.5 rounded-2xl hover:bg-maroon-800 transition-colors"
              >
                <Share2 size={16} strokeWidth={2} />
                Share with your community
              </button>
            </>
          )}
        </div>
      )}

      {/* Filter panel overlay */}
      {showFilterPanel && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-maroon-900/60 backdrop-blur-sm" onClick={() => setShowFilterPanel(false)} />
          <div className="relative bg-ivory-25 rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-float animate-fade-up border border-gold-500/30">
            <div className="sticky top-0 bg-ivory-25 border-b border-ivory-200 px-5 py-4 flex items-center justify-between rounded-t-3xl">
              <h2 className="font-display font-semibold text-xl text-ink-900">Filters</h2>
              <button onClick={() => setShowFilterPanel(false)}>
                <X size={20} className="text-ink-400" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Age */}
              <div>
                <label className="label">Age Range</label>
                <div className="flex gap-3">
                  <input type="number" min={21} max={80} className="input" placeholder="Min age (21+)" value={pendingFilters.minAge}
                    onChange={(e) => setF('minAge', e.target.value)} />
                  <input type="number" min={21} max={80} className="input" placeholder="Max age" value={pendingFilters.maxAge}
                    onChange={(e) => setF('maxAge', e.target.value)} />
                </div>
              </div>
              {/* Height */}
              <div>
                <label className="label">Height (cm)</label>
                <div className="flex gap-3">
                  <input type="number" className="input" placeholder="Min height" value={pendingFilters.minHeightCm}
                    onChange={(e) => setF('minHeightCm', e.target.value)} />
                  <input type="number" className="input" placeholder="Max height" value={pendingFilters.maxHeightCm}
                    onChange={(e) => setF('maxHeightCm', e.target.value)} />
                </div>
              </div>
              {/* Education */}
              <div>
                <label className="label">Minimum Education</label>
                <select className="input" value={pendingFilters.educationLevel} onChange={(e) => setF('educationLevel', e.target.value)}>
                  <option value="">Any</option>
                  <option value="TENTH">10th Pass</option>
                  <option value="TWELFTH">12th Pass</option>
                  <option value="GRADUATE">Graduate</option>
                  <option value="POST_GRADUATE">Post Graduate</option>
                  <option value="DOCTORATE">Doctorate</option>
                </select>
              </div>
              {/* Family Type */}
              <div>
                <label className="label">Family Type</label>
                <select className="input" value={pendingFilters.familyType} onChange={(e) => setF('familyType', e.target.value)}>
                  <option value="">Any</option>
                  <option value="JOINT">Joint Family</option>
                  <option value="NUCLEAR">Nuclear Family</option>
                </select>
              </div>
              {/* Diet */}
              <div>
                <label className="label">Diet</label>
                <select className="input" value={pendingFilters.diet} onChange={(e) => setF('diet', e.target.value)}>
                  <option value="">Any</option>
                  <option value="VEGETARIAN">Vegetarian</option>
                  <option value="NON_VEGETARIAN">Non-Vegetarian</option>
                  <option value="EGGETARIAN">Eggetarian</option>
                </select>
              </div>
              {/* Marital Status */}
              <div>
                <label className="label">Marital Status</label>
                <select className="input" value={pendingFilters.maritalStatus} onChange={(e) => setF('maritalStatus', e.target.value)}>
                  <option value="">Any</option>
                  <option value="NEVER_MARRIED">Never Married</option>
                  <option value="DIVORCED">Divorced</option>
                  <option value="WIDOWED">Widowed</option>
                  <option value="AWAITING_DIVORCE">Awaiting Divorce</option>
                </select>
              </div>
              {/* Income */}
              <div>
                <label className="label">Minimum Income (LPA)</label>
                <input type="number" className="input" placeholder="e.g., 5" value={pendingFilters.minIncomeLpa}
                  onChange={(e) => setF('minIncomeLpa', e.target.value)} />
              </div>
            </div>
            <div className="sticky bottom-0 bg-ivory-25 border-t border-ivory-200 px-5 py-4 flex gap-3">
              <button onClick={clearFilters} className="btn-secondary flex-1 py-3">Clear</button>
              <button onClick={applyFilters} className="btn-primary flex-1 py-3">Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
